'use client';

import { Button, Icon, RichText, ShareModal, variables } from 'daskapital-ds';
import { IPost } from '@/lib/wordpress/_types/archiveGrid';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useGlobalContext } from '@/lib/globalContext';

const MAX_CONTENT_HEIGHT = 200;
const FAQ_ACTIVE_SHADOW = '0 8px 24px rgba(1, 37, 59, 0.15)';

const StyledFaqCard = styled.div<{ $active: boolean }>`
	background: white;
	width: 100%;
	border-radius: 4px;
	overflow: hidden;
	transition: box-shadow 0.3s ease;
	box-shadow: ${({ $active }) => ($active ? FAQ_ACTIVE_SHADOW : 'none')};

	&:hover {
		box-shadow: ${FAQ_ACTIVE_SHADOW};
	}
`;

const StyledFaqHeader = styled.button<{ $active: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	min-height: 60px;
	padding: 12px;
	gap: 8px;
	background: none;
	border: none;
	text-align: left;
	cursor: pointer;
	font: inherit;
	color: inherit;

	span {
		font-weight: ${({ $active }) => ($active ? 700 : 400)};
	}

	&:hover span {
		font-weight: 700;
	}

	svg {
		flex-shrink: 0;
		transition: transform 0.3s ease;
		transform: rotate(${({ $active }) => ($active ? '180deg' : '0deg')});
	}
`;

const StyledFaqContent = styled.div<{ $active: boolean; $height: number; $fade: boolean }>`
	position: relative;
	max-height: ${({ $active, $height }) => ($active ? `${$height}px` : '0px')};
	overflow: hidden;
	transition: max-height 0.3s ease;

	${({ $fade }) => $fade && css`
		&::after {
			content: '';
			position: absolute;
			left: 0;
			right: 0;
			bottom: 0;
			height: 56px;
			background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
			pointer-events: none;
		}
	`}
`;

const StyledFaqContentInner = styled.div`
	padding: 0 12px;

	& > * > * + * {
		margin-top: 12px !important;
	}

	& li {
		margin: 12px 0;
	}
`;

const StyledFaqActions = styled.div`
	display: flex;
	align-items: stretch;
	flex-wrap: wrap;
	gap: 12px;
	margin-top: 20px;
	padding: 0 12px 16px;
`;

const StyledActionButton = styled(Button)`
	flex: 1 1 0%;
	justify-content: center;
	min-width: 140px;
	height: 40px;
`;

// ShareModal's own text color relies on a color token that doesn't exist in
// the design system, so it silently falls back to whatever text color the
// page happens to inherit (e.g. white on a style-dark page like /help-center)
const StyledFaqShareModal = styled(ShareModal)`
	color: ${variables.colors.gray70} !important;
`;

/**
 *
 * @param {Post} {post}
 * @returns {JSX.Element} // A FAQ accordion item: click the question to expand the answer
 */
export const FaqCard = ({
	post,
	isOpen,
	onToggle,
}: {
	post: IPost;
	isOpen?: boolean;
	onToggle?: () => void;
}) => {
	const state = useGlobalContext();
	const [localActive, setLocalActive] = useState(false);
	const active = isOpen !== undefined ? isOpen : localActive;
	const toggleActive = onToggle || (() => setLocalActive(!localActive));
	const [isOverflowing, setIsOverflowing] = useState(false);
	const [shareOpen, setShareOpen] = useState(false);
	const contentInnerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (contentInnerRef.current) {
			setIsOverflowing(contentInnerRef.current.scrollHeight > MAX_CONTENT_HEIGHT);
		}
	}, [post.excerpt]);

	const contentHeight = Math.min(contentInnerRef.current?.scrollHeight || MAX_CONTENT_HEIGHT, MAX_CONTENT_HEIGHT);

	return (
		<StyledFaqCard $active={active}>
			<StyledFaqHeader
				type="button"
				onClick={toggleActive}
				aria-expanded={active}
				$active={active}
			>
				<span>{post.title}</span>
				<svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M15.8107 0L0.1875 0L0.1875 3.22479L7.59457 3.22479L0.1875 9.8357L0.1875 14L7.99847 7.01344L15.8103 14V9.8357L8.40362 3.22479L15.8107 3.22479V0Z"
						fill="#000000"
					/>
				</svg>
			</StyledFaqHeader>
			<StyledFaqContent $active={active} $height={contentHeight} $fade={active && isOverflowing}>
				<StyledFaqContentInner ref={contentInnerRef}>
					<RichText tag="div" className="single-postcontent">{post.excerpt as string}</RichText>
				</StyledFaqContentInner>
			</StyledFaqContent>
			{active && (
				<StyledFaqActions>
					{isOverflowing && (
						<StyledActionButton
							href={post.slug as string}
							ariaLabel={`Read more about ${post.title}`}
							variant="secondary"
							size="small"
							outlined
						>
							{state?.dict?.common?.seeMore}
						</StyledActionButton>
					)}
					<StyledActionButton
						variant="secondary"
						size="small"
						outlined
						leftIcon={<Icon icon="icon-share" />}
						onClick={() => setShareOpen(true)}
						ariaLabel={`Share ${post.title}`}
					>
						{state?.dict?.helpCenter?.shareFaq}
					</StyledActionButton>
				</StyledFaqActions>
			)}
			<StyledFaqShareModal
				siteTitle={process.env.NEXT_PUBLIC_SITE_NAME}
				title={post.title as string}
				url={`${process.env.NEXT_PUBLIC_FE_URL}${post.slug}`}
				label={state?.dict?.helpCenter?.shareFaq}
				copyText={state?.dict?.helpCenter?.shareCopy}
				open={shareOpen}
				onClose={() => setShareOpen(false)}
			/>
		</StyledFaqCard>
	);
};
